<template>
  <div class="barn-sensors-strip">
    <a
      v-if="camSensors.length > 0"
      class="barn-sensors-strip__item"
      :class="camColorClass"
      title="Dado mais recente da SmartCam"
      @click.prevent="goTo('/batch-details/smart-cam')"
    >
      <span class="barn-sensors-strip__brand">
        <img src="../assets/kg-white.svg" alt="" class="barn-sensors-strip__icon" />
        <span class="barn-sensors-strip__label">SMARTCAM</span>
      </span>
      <AnimadePlaceHolder v-if="loadingCam || batchLoading" height="20px" width="120px" borderRadius="4px" />
      <template v-else>
        <span class="barn-sensors-strip__value">{{ camText }}</span>
        <span v-if="camDate" class="barn-sensors-strip__meta">{{ camDate }}</span>
      </template>
    </a>

    <a
      v-if="micSensors.length > 0"
      class="barn-sensors-strip__item"
      :class="micColorClass"
      title="Média de tosses de ontem"
      @click.prevent="goTo('/batch-details/smart-mic')"
    >
      <span class="barn-sensors-strip__brand">
        <img src="../assets/mic-white.svg" alt="" class="barn-sensors-strip__icon" />
        <span class="barn-sensors-strip__label">SMARTMIC</span>
      </span>
      <AnimadePlaceHolder v-if="loadingMic || batchLoading" height="20px" width="120px" borderRadius="4px" />
      <template v-else>
        <span class="barn-sensors-strip__value">{{ micText }}</span>
        <span v-if="micDate" class="barn-sensors-strip__meta">{{ micDate }}</span>
      </template>
    </a>

    <a
      v-if="senseSensors.length > 0"
      class="barn-sensors-strip__item"
      :class="senseColorClass"
      title="Temperatura e umidade do SmartSense"
      @click.prevent="goTo('/batch-details/smart-sense')"
    >
      <span class="barn-sensors-strip__brand">
        <img src="../assets/thermometer.svg" alt="" class="barn-sensors-strip__icon barn-sensors-strip__icon--sense" />
        <span class="barn-sensors-strip__label">SMARTSENSE</span>
      </span>
      <AnimadePlaceHolder v-if="loadingSense" height="20px" width="120px" borderRadius="4px" />
      <template v-else>
        <span class="barn-sensors-strip__value">{{ senseText }}</span>
      </template>
    </a>
  </div>
</template>

<script>
import { apiRoutes } from '../utils/api';
import { pickSmartMicSensor, smartMicSensorKey } from '../utils/smartmic';
import AnimadePlaceHolder from './AnimadePlaceHolder.vue';
import { format, parseISO } from 'date-fns';
import { isSmartSenseSensor, smartsenseApi } from '../constants/smartsense';

export default {
  name: 'BarnSensorsStrip',
  components: { AnimadePlaceHolder },
  props: {
    barn: { type: Object, required: true },
    batch: { type: Array, default: () => [] },
    batchLoading: { type: Boolean, default: false },
    camSensors: { type: Array, default: () => [] },
    micSensors: { type: Array, default: () => [] },
    senseSensors: { type: Array, default: () => [] },
  },
  data() {
    return {
      loadingCam: true,
      loadingMic: true,
      loadingSense: true,
      micFetchGen: 0,
      senseSummary: { error: true, temperature: null, humidity: null, message: null },
    };
  },
  computed: {
    dataCam() {
      const entry = this.$store.getters.getSensorsListCam.find((obj) => {
        if (this.batch[0]?.pk) return obj.batch?.pk == this.batch[0].pk;
        return obj.barn?.pk == this.barn?.pk;
      });
      return entry?.data ?? [];
    },
    dataMic() {
      const entry = this.$store.getters.getSensorsListMic.find((obj) => {
        if (this.batch[0]?.pk) return obj.batch?.pk == this.batch[0].pk;
        return obj.barn?.pk == this.barn?.pk;
      });
      return entry?.data ?? [];
    },
    camText() {
      if (this.batchLoading && !this.batch[0]) return null;
      if (this.dataCam.length === 0) return 'SEM DADOS';
      if (!this.batch[0]) return 'SEM LOTE';
      if (this.batch[0].date_departure !== null) return 'SEM LOTE';
      const weight = Math.round(this.dataCam[0].weight);
      const pct = (this.dataCam[0].weight * 100) / this.dataCam[0].reference_weight;
      return `${weight} kg | ${(pct - 100).toFixed(1)}%`;
    },
    camDate() {
      if (this.dataCam.length === 0 || !this.batch[0] || this.batch[0].date_departure !== null) return null;
      const raw = this.dataCam[0]?.datetime_fetched
        || this.dataCam[0]?.datetime_last_ping
        || this.dataCam[0]?.date;
      return this.formatDate(raw, 'dd-MM-yyyy');
    },
    camColorClass() {
      if (this.camText === 'SEM DADOS' || this.camText === 'SEM LOTE') return 'is-disabled';
      if (this.dataCam.length === 0) return 'is-disabled';
      const pct = (Math.round(this.dataCam[0].weight) * 100) / this.dataCam[0].reference_weight;
      if (pct > 105) return 'is-above';
      if (pct >= 100) return 'is-good';
      if (pct >= 95) return 'is-medium';
      return 'is-bad';
    },
    micText() {
      if (this.batchLoading && !this.batch[0]) return null;
      if (this.batch[0]?.date_departure != null) return 'SEM LOTE';
      if (this.dataMic.length === 0) return 'SEM DADOS';
      if (this.dataMic[0].message !== undefined) return 'SEM DADOS';
      return String(this.dataMic[0].last_coughs_count);
    },
    micDate() {
      if (this.micText === 'SEM DADOS' || this.micText === 'SEM LOTE') return null;
      if (this.dataMic.length === 0) return null;
      const raw = this.dataMic[0]?.datetime_last_ping
        || this.dataMic[0]?.datetime_fetched
        || this.dataMic[0]?.datetime;
      return this.formatDate(raw, 'dd-MM-yyyy');
    },
    micColorClass() {
      if (this.micText === 'SEM DADOS' || this.micText === 'SEM LOTE') return 'is-disabled';
      const sum = Number(this.micText) / 8;
      if (sum >= 16) return 'is-bad';
      if (sum >= 8) return 'is-medium';
      return 'is-good';
    },
    micSensorKey() {
      return smartMicSensorKey(this.micSensors);
    },
    senseText() {
      if (this.senseSummary.message === 'SEM LOTE') return 'SEM LOTE';
      if (this.senseSummary.error) return 'SEM DADOS';
      const parts = [];
      if (this.senseSummary.temperature != null) {
        parts.push(`${Number(this.senseSummary.temperature).toFixed(1)} ºC`);
      }
      if (this.senseSummary.humidity != null) {
        parts.push(`${Number(this.senseSummary.humidity).toFixed(1)}% UR`);
      }
      return parts.length > 0 ? parts.join(' | ') : 'SEM DADOS';
    },
    senseColorClass() {
      if (this.senseText === 'SEM DADOS' || this.senseText === 'SEM LOTE') return 'is-disabled';
      return 'is-sense';
    },
  },
  methods: {
    formatDate(date, pattern) {
      if (!date) return null;
      try {
        return format(parseISO(date), pattern);
      } catch {
        try {
          return format(new Date(date), pattern);
        } catch {
          return null;
        }
      }
    },
    goTo(route) {
      this.$store.commit('setBarnTarget', this.barn);
      this.$store.commit('setBatchTarget', this.batch[0] ?? null);
      this.$router.push(route);
    },
    async fetchCam() {
      if (this.camSensors.length === 0) {
        this.loadingCam = false;
        return;
      }
      if (this.dataCam.length > 0) {
        this.loadingCam = false;
        return;
      }
      try {
        this.$store.commit('incrementActiveRequests');
        const element = this.camSensors[0];
        const response = await fetch(
          apiRoutes.smartCamHome(element.pk),
          { headers: this.$store.getters.getHeader }
        );
        const json = await response.json();
        this.$store.commit('setSensorsListCam', {
          batch: this.batch[0],
          sensor: this.camSensors,
          barn: this.barn,
          data: response.ok === false ? [] : [json],
        });
      } finally {
        this.loadingCam = false;
        this.$store.commit('decrementActiveRequests');
      }
    },
    async fetchMic({ force = false } = {}) {
      const element = pickSmartMicSensor(this.micSensors);
      if (!element?.pk) {
        this.loadingMic = false;
        return;
      }
      if (!force && this.dataMic.length > 0) {
        this.loadingMic = false;
        return;
      }
      const gen = ++this.micFetchGen;
      try {
        this.$store.commit('incrementActiveRequests');
        const response = await fetch(
          apiRoutes.smartMicHome(element.pk),
          { headers: this.$store.getters.getHeader }
        );
        const json = await response.json();
        if (gen !== this.micFetchGen) return;
        this.$store.commit('setSensorsListMic', {
          batch: this.batch[0],
          sensor: this.micSensors,
          barn: this.barn,
          data: [json],
        });
      } finally {
        if (gen === this.micFetchGen) this.loadingMic = false;
        this.$store.commit('decrementActiveRequests');
      }
    },
    pickSenseSensorPk() {
      const list = (this.senseSensors || []).filter(isSmartSenseSensor);
      const sensor = list[0] || this.senseSensors[0];
      return sensor?.pk ?? sensor?.id ?? null;
    },
    async fetchSense() {
      if (this.senseSensors.length === 0) {
        this.loadingSense = false;
        return;
      }
      const sensorPk = this.pickSenseSensorPk();
      if (sensorPk == null) {
        this.loadingSense = false;
        this.senseSummary = { error: true, temperature: null, humidity: null, message: null };
        return;
      }
      try {
        this.$store.commit('incrementActiveRequests');
        const response = await fetch(smartsenseApi.lastData(sensorPk), {
          headers: this.$store.getters.getHeader,
        });
        if (response.status === 409) {
          this.senseSummary = {
            error: true,
            temperature: null,
            humidity: null,
            message: 'SEM LOTE',
          };
          return;
        }
        if (!response.ok) {
          this.senseSummary = { error: true, temperature: null, humidity: null, message: null };
          return;
        }
        const json = await response.json();
        if (json == null) {
          this.senseSummary = { error: true, temperature: null, humidity: null, message: null };
          return;
        }
        const temp = json.temperature != null ? Number(json.temperature) : null;
        const hum = json.humidity != null ? Number(json.humidity) : null;
        const hasTemp = temp != null && !Number.isNaN(temp);
        const hasHum = hum != null && !Number.isNaN(hum);
        if (!hasTemp && !hasHum) {
          this.senseSummary = { error: true, temperature: null, humidity: null, message: null };
          return;
        }
        this.senseSummary = {
          error: false,
          temperature: hasTemp ? temp : null,
          humidity: hasHum ? hum : null,
          message: null,
        };
      } catch {
        this.senseSummary = { error: true, temperature: null, humidity: null, message: null };
      } finally {
        this.loadingSense = false;
        this.$store.commit('decrementActiveRequests');
      }
    },
  },
  watch: {
    micSensorKey(next, prev) {
      if (!next || next === prev) return;
      this.fetchMic({ force: true });
    },
  },
  created() {
    this.fetchCam();
    this.fetchMic();
    this.fetchSense();
  },
};
</script>

<style scoped>
.barn-sensors-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.barn-sensors-strip__item {
  flex: 1 1 180px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px 14px;
  border-radius: 6px;
  text-decoration: none;
  color: #fff;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease;
}

.barn-sensors-strip__item:hover {
  filter: brightness(0.93);
  transform: translateY(-1px);
}

.barn-sensors-strip__brand {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.barn-sensors-strip__icon {
  width: 18px;
  height: 18px;
  opacity: 0.95;
}

.barn-sensors-strip__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.barn-sensors-strip__value {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.barn-sensors-strip__meta {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.92;
  white-space: nowrap;
}

.barn-sensors-strip__item.is-good { background: #a1cd7b; }
.barn-sensors-strip__item.is-medium { background: #e2bf65; }
.barn-sensors-strip__item.is-bad { background: #a94949; }
.barn-sensors-strip__item.is-above { background: #415430; }
.barn-sensors-strip__item.is-disabled { background: #adadad; }
.barn-sensors-strip__item.is-sense { background: #1b4238; }

.barn-sensors-strip__icon--sense {
  filter: brightness(0) invert(1);
}

@media screen and (max-width: 640px) {
  .barn-sensors-strip__item {
    flex: 1 1 100%;
    flex-wrap: wrap;
    row-gap: 4px;
  }

  .barn-sensors-strip__value {
    flex: 1 1 auto;
    text-align: left;
  }

  .barn-sensors-strip__meta {
    margin-left: auto;
  }
}
</style>
